import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Appearance } from 'design/types';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { CalculatorRules, generateCalculatorRulesRequest, updateCalculatorRulesRequest } from 'store/CalculatorRules';
import { IStore } from 'store/types';
import { DEFAULT_POOL_NAME, ICreateUpdateCalculatorRules } from 'types';
import { toTitleCase } from 'utils';
import { AccordionSection, AlertHeader, Button, Message, ModalFactory, ModalFooter, ModalTitle, Text, TipShareBreakdown } from '../../../../components';
import { theme, toRem } from '../../../../design';
import { LoadingText, ModalContent, StyledSpinner } from './styles';

interface IAIRulesModalProps {
  siteId: string;
  onClose(): void;
  dataCy?: string;
}

const Content = styled(ModalContent)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${toRem(16)};

  p.center {
    text-align: center;
  }
`;

const RulesLayout = styled.section`
  flex-direction: column;
  width: 100%;

  .tip-share {
    margin-bottom: ${toRem(8)};
  }

  .tip-share-content {
    background-color: ${theme.color.neutral[50]};
    padding: 0 ${toRem(16)};

    .rules-container {
      background-color: ${theme.color.white};
      border: 1px solid ${theme.color.neutral[200]};
      margin-bottom: ${toRem(16)};
      width: 100%;
    }
  }
`;

export const AIRulesModal: React.FC<IAIRulesModalProps> = ({ siteId, onClose, dataCy = 'ai-rules-modal' }) => {
  const isMounted = useRef(false);
  const dispatch = useDispatch();
  const [rules, setRules] = useState<ICreateUpdateCalculatorRules>(null);
  const [error, setError] = useState<any>(null);
  const isGeneratingRules = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, CalculatorRules.GENERATE_CALCULATOR_RULES_REQUEST));
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, CalculatorRules.UPDATE_CALCULATOR_RULES_REQUEST));

  const handleSuccess = (rules: ICreateUpdateCalculatorRules) => {
    setRules(rules);
  };

  const handleError = (error: any) => {
    setError(error);
  };

  const handleSave = (siteId: string, rules: ICreateUpdateCalculatorRules) => {
    dispatch(updateCalculatorRulesRequest(siteId, rules, handleSaveSuccess, 'Calculator rules saved'));
  };

  const handleSaveSuccess = () => {
    onClose();
  };

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      dispatch(generateCalculatorRulesRequest(siteId, handleSuccess, handleError));
    }
  }, [dispatch, siteId]);

  return (
    <ModalFactory dataCy={dataCy} onClose={onClose} isActive maxWidth={600} closeOnOutSideClick={false}>
      <Content>
        {isGeneratingRules && (
          <>
            <ModalTitle>Generating Tip Calculator Rules</ModalTitle>
            <StyledSpinner color={theme.color.secondary[400]} size={64} stroke={6} />
            <LoadingText>Please wait while we generate the rules</LoadingText>
          </>
        )}
        {!isGeneratingRules && rules && (
          <>
            <ModalTitle>Review Tip Calculator Rules</ModalTitle>
            <Text size="small" className="center">
              The rules below have been generated by reviewing your POS data. Please view before applying these rules.
            </Text>
            <RulesLayout>
              {rules.rules
                .filter((rule) => rule.sharePolicy)
                .map((rule, i) => (
                  <AccordionSection key={`rule-${i}`} compact open={false} title={rule.default ? DEFAULT_POOL_NAME : `${toTitleCase(rule.contributor?.name)} Share`} className="tip-share" dataCy={`${dataCy}-rule`}>
                    <TipShareBreakdown tipShare={rule} isAdvanced={false} className="rules-container" />
                  </AccordionSection>
                ))}
            </RulesLayout>
            <ModalFooter columns={2}>
              <Button appearance="white" label="Cancel" data-cy={`${dataCy}-cancel-button`} onClick={() => onClose()} />
              <Button appearance="primary" label="Save" data-cy={`${dataCy}-save-button`} isProcessing={isLoading} onClick={() => handleSave(siteId, rules)} />
            </ModalFooter>
          </>
        )}
        {!isGeneratingRules && !rules && error && (
          <>
            <AlertHeader size={64} />
            <ModalTitle>We are having trouble importing</ModalTitle>
            <Text>
              We encountered an issue generating the calculator
              <br />
              rules for this site.
            </Text>
            {error.errorMessages.length && <Message className="message" appearance={Appearance.WARNING} message={error.errorMessages[0]} />}
          </>
        )}
      </Content>
    </ModalFactory>
  );
};
